import React, { useState } from 'react';
import { WidgetSettings } from '../types/quiz';

interface EmailCaptureStepProps {
  onSubmit: (data: { parent_name: string; child_name: string; email: string }) => void;
  loading: boolean;
  settings: WidgetSettings;
}

interface FormData {
  parent_name: string;
  child_name: string;
  email: string;
}

interface FormErrors {
  parent_name?: string;
  child_name?: string;
  email?: string;
}

const EmailCaptureStep: React.FC<EmailCaptureStepProps> = ({ onSubmit, loading, settings }) => {
  const primaryColor = settings.primary_color || '#4F46E5';
  const accentColor = settings.accent_color || '#10B981';

  const [formData, setFormData] = useState<FormData>({
    parent_name: '',
    child_name: '',
    email: ''
  });

  const [errors, setErrors] = useState<FormErrors>({});

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.parent_name.trim()) {
      newErrors.parent_name = 'Parent name is required';
    }

    if (!formData.child_name.trim()) {
      newErrors.child_name = 'Child name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="container mx-auto px-6 py-12 sm:py-16 md:py-20">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8 md:mb-10">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4 md:mb-5 leading-tight px-2">
              You're almost there.
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 mb-4 px-2">
              Get your child's personalized profile sent right to your inbox.
            </p>
          </div>

          {/* Benefits List - Same Style as Landing Page */}
          <div className="mb-8 md:mb-10 bg-white rounded-lg p-6 sm:p-8 md:p-10 shadow-sm">
            <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-6 md:mb-8">
              What you'll get:
            </h3>

            <div className="space-y-4 md:space-y-5">
              <div className="flex items-start gap-3 md:gap-4">
                <svg className="w-5 h-5 md:w-6 md:h-6 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24" style={{ color: accentColor }}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                <p className="text-base md:text-lg text-gray-800">
                  <strong>Your child's complete learning profile</strong> — what they need, what works, what doesn't
                </p>
              </div>

              <div className="flex items-start gap-3 md:gap-4">
                <svg className="w-5 h-5 md:w-6 md:h-6 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24" style={{ color: accentColor }}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                <p className="text-base md:text-lg text-gray-800">
                  <strong>Actionable next steps</strong> — things you can start doing this week
                </p>
              </div>

              <div className="flex items-start gap-3 md:gap-4">
                <svg className="w-5 h-5 md:w-6 md:h-6 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24" style={{ color: accentColor }}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                <p className="text-base md:text-lg text-gray-800">
                  <strong>Free resource guides</strong> — tailored specifically to your child's profile
                </p>
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="bg-white rounded-lg p-6 sm:p-8 md:p-10 shadow-sm">
            {/* Parent Name */}
            <div className="mb-5 md:mb-6">
              <label htmlFor="parent_name" className="block text-sm font-semibold text-gray-700 mb-2">
                Your first name *
              </label>
              <input
                type="text"
                id="parent_name"
                value={formData.parent_name}
                onChange={(e) => handleChange('parent_name', e.target.value)}
                className={`w-full px-4 py-3 md:py-4 rounded-lg border-2 transition-colors text-base md:text-lg ${
                  errors.parent_name
                    ? 'border-red-500 focus:border-red-600'
                    : 'border-gray-300 focus:border-indigo-500'
                } focus:outline-none`}
                style={{ fontSize: '16px' }}
                placeholder="Your name"
                disabled={loading}
              />
              {errors.parent_name && (
                <p className="mt-2 text-sm text-red-600">{errors.parent_name}</p>
              )}
            </div>

            {/* Child Name */}
            <div className="mb-5 md:mb-6">
              <label htmlFor="child_name" className="block text-sm font-semibold text-gray-700 mb-2">
                Your child's first name *
              </label>
              <input
                type="text"
                id="child_name"
                value={formData.child_name}
                onChange={(e) => handleChange('child_name', e.target.value)}
                className={`w-full px-4 py-3 md:py-4 rounded-lg border-2 transition-colors text-base md:text-lg ${
                  errors.child_name
                    ? 'border-red-500 focus:border-red-600'
                    : 'border-gray-300 focus:border-indigo-500'
                } focus:outline-none`}
                style={{ fontSize: '16px' }}
                placeholder="Your child's name"
                disabled={loading}
              />
              {errors.child_name && (
                <p className="mt-2 text-sm text-red-600">{errors.child_name}</p>
              )}
            </div>

            {/* Email */}
            <div className="mb-5 md:mb-6">
              <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                Email address *
              </label>
              <input
                type="email"
                id="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                className={`w-full px-4 py-3 md:py-4 rounded-lg border-2 transition-colors text-base md:text-lg ${
                  errors.email
                    ? 'border-red-500 focus:border-red-600'
                    : 'border-gray-300 focus:border-indigo-500'
                } focus:outline-none`}
                style={{ fontSize: '16px' }}
                placeholder="your.email@example.com"
                disabled={loading}
              />
              {errors.email && (
                <p className="mt-2 text-sm text-red-600">{errors.email}</p>
              )}
            </div>

            {/* Privacy Note */}
            <p className="text-sm text-gray-500 text-center mb-5 md:mb-6 px-2">
              We respect your privacy. Unsubscribe anytime.
            </p>

            {/* Submit Button - Same Style as Landing Page */}
            <button
              type="submit"
              disabled={loading}
              className="w-full px-8 py-4 md:py-5 rounded-xl text-white font-bold text-lg md:text-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 min-h-[56px]"
              style={{
                backgroundColor: primaryColor
              }}
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Generating Your Profile...
                </span>
              ) : (
                'Get My Child\'s Profile →'
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EmailCaptureStep;
